import { useState } from "react";
import {
  Waves, Kanban, Zap, BarChart2, CheckSquare, ArrowRight, Star, Sparkles,
  Users, TrendingUp, Building2,
} from "lucide-react";
import { ScrollingStrip } from "./LandingHelpers";
import { HERO_IMAGES, REVIEW_BADGES, CLIENT_LOGOS } from "./LandingContent";

function AppMockup({ active = "dashboard" }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-rose-400" />
          <div className="w-3 h-3 rounded-full bg-amber-400" />
          <div className="w-3 h-3 rounded-full bg-emerald-400" />
        </div>
        <div className="flex-1 mx-4 bg-white border border-slate-200 rounded-md px-3 py-1 text-xs text-slate-400 text-center">
          app.pipe4.com/dashboard
        </div>
      </div>
      <div className="flex h-[420px] sm:h-[480px] bg-[#f8fafc]">
        <div className="w-44 bg-white border-r border-slate-200 flex-shrink-0 p-3 space-y-0.5">
          <div className="flex items-center gap-2 mb-4 px-2">
            <img src="/brand/icon-4.png" alt="Pipe4.me" className="w-6 h-6 rounded" />
            <div><p className="text-xs font-bold text-slate-900">Pipe4.me</p><p className="text-[9px] text-slate-400">pipeline</p></div>
          </div>
          {[
            { icon: BarChart2, k: "dashboard", l: "Dashboard" },
            { icon: Kanban, k: "deals", l: "Deals" },
            { icon: Building2, k: "companies", l: "Companies" },
            { icon: Users, k: "contacts", l: "Contacts" },
            { icon: CheckSquare, k: "tasks", l: "Tasks" },
            { icon: Zap, k: "auto", l: "Automations", badge: "BETA" },
            { icon: TrendingUp, k: "reports", l: "Reports" },
          ].map(item => (
            <div key={item.k} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] font-medium ${item.k === active ? "bg-[#3B82F6] text-white" : "text-slate-500"}`}>
              <item.icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
              <span className="truncate">{item.l}</span>
              {item.badge && <span className="text-[8px] bg-white/20 px-1 rounded ml-auto">{item.badge}</span>}
            </div>
          ))}
        </div>
        <div className="flex-1 p-4 overflow-hidden">
          <div className="mb-3">
            <h2 className="text-sm font-bold text-slate-900">Dashboard</h2>
            <p className="text-[10px] text-slate-400">Sales performance in real time</p>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              { l: "OPEN MRR", v: "R$ 75k", s: "31 deals", c: "#3B82F6" },
              { l: "WON MRR", v: "R$ 20k", s: "\u2191 625% vs prev", c: "#10b981" },
              { l: "WIN RATE", v: "50%", s: "9 won \u00b7 9 lost", c: "#f59e0b" },
            ].map(k => (
              <div key={k.l} className="bg-white rounded-xl border border-slate-200 p-2.5 shadow-sm">
                <p className="text-[8px] font-bold text-slate-400 uppercase mb-1">{k.l}</p>
                <p className="text-sm font-bold text-slate-900">{k.v}</p>
                <p className="text-[8px] mt-0.5" style={{ color: k.c }}>{k.s}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <p className="text-[10px] font-semibold text-slate-600 mb-2">Sales Funnel</p>
            <div className="space-y-1.5">
              {[["Prospecting","85%","#ef4444"],["Qualified","65%","#f97316"],["Proposal","45%","#f59e0b"],["Closing","28%","#10b981"]].map(([l,w,c])=>(
                <div key={l} className="flex items-center gap-2">
                  <p className="text-[8px] text-slate-400 w-14 text-right">{l}</p>
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5"><div className="h-1.5 rounded-full" style={{width:w,backgroundColor:c}}/></div>
                  <p className="text-[8px] text-slate-400 w-5">{w}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingHero({ c, lang, handleCTA }) {
  const [heroImg] = useState(() => Math.floor(Math.random() * HERO_IMAGES.length));

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center" data-testid="landing-hero">
        <div className="absolute inset-0"
          style={{ backgroundImage: `url(${HERO_IMAGES[heroImg]})`, backgroundSize: "cover", backgroundPosition: "center center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-10">
            <div className="inline-block bg-white/50 backdrop-blur-md rounded-3xl px-6 sm:px-14 py-10 border border-white/80 shadow-2xl shadow-blue-100/30 mb-8 max-w-3xl w-full">
              <div className="inline-flex items-center gap-2 bg-blue-50/80 text-[#3B82F6] text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-100 mb-6">
                <Sparkles className="w-3.5 h-3.5" />{c.hero.badge}
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-heading text-slate-900 tracking-tight leading-tight mb-5">
                {c.hero.h1a}<br /><span className="text-[#3B82F6]">{c.hero.h1b}</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-8">{c.hero.sub}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
                <button onClick={handleCTA} data-testid="hero-cta-btn"
                  className="flex items-center gap-2 px-7 py-3.5 bg-[#3B82F6] text-white rounded-xl text-base font-semibold hover:bg-blue-600 transition-all shadow-lg shadow-blue-300/50 hover:shadow-xl hover:-translate-y-0.5">
                  {c.hero.cta1} <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => { const el = document.getElementById("como-funciona"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className="flex items-center gap-2 px-7 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-slate-200 text-slate-700 rounded-xl text-base font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-all shadow-md">
                  {c.hero.cta2}
                </button>
              </div>
              <p className="text-xs text-slate-500">{c.hero.hint}</p>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {REVIEW_BADGES.map(b => (
                <div key={b.platform} className="flex items-center gap-2.5 bg-white/80 backdrop-blur-sm border border-white/90 rounded-xl px-3.5 py-2 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-slate-800 leading-none">{b.stars} / 5</p>
                    <p className="text-[10px] text-slate-500 leading-snug">{b.platform} &middot; {b.reviews}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative max-w-5xl mx-auto">
            <AppMockup />
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-blue-300 rounded-full blur-3xl opacity-25" />
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-slate-100 bg-white py-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {c.stats.map((stat, i) => (
              <div key={i} className="space-y-1">
                <p className="text-3xl font-bold text-[#3B82F6]">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGO STRIP */}
      <div className="bg-white border-b border-slate-100 py-3">
        <p className="text-center text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
          {lang === "pt-BR" ? "Times de vendas que confiam no Pipe4.me" : lang === "en-US" ? "Sales teams that trust Pipe4.me" : "Equipos de ventas que confían en Pipe4.me"}
        </p>
        <ScrollingStrip
          items={CLIENT_LOGOS} speed={35} bg="bg-white" border={false}
          renderItem={(name) => (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-100 bg-slate-50 hover:bg-blue-50 hover:border-blue-100 transition-colors">
              <div className="w-6 h-6 rounded-md bg-[#3B82F6]/10 flex items-center justify-center">
                <Building2 className="w-3.5 h-3.5 text-[#3B82F6]" />
              </div>
              <span className="text-sm font-semibold text-slate-600 whitespace-nowrap">{name}</span>
            </div>
          )}
        />
      </div>
    </>
  );
}
